/**
 * Extrai a mensagem de erro de uma troca de estado do workflow.
 *
 * Nesta versão do Volto o middleware de API devolve a promise original do
 * `helpers/Api/Api.js`, que o superagent REJEITA em resposta 4xx/5xx
 * (`middleware/api.js` dispara a ação `${TYPE}_FAIL` como efeito colateral,
 * mas a promise retorna rejeitada). Ou seja: o erro pode chegar de dois
 * jeitos — como ação `_FAIL` resolvida (erro dentro de `action.error`) ou
 * como rejeição com o erro do superagent. Este helper aceita os dois.
 *
 * A mensagem útil vem do `plone.restapi`, que converte a `WorkflowException`
 * levantada pelo backend em `400 {"error": {"message": ...}}`.
 *
 * Além da recusa de negócio, cobrimos dois casos em que o texto cru não
 * ajuda o editor:
 *
 * * falha de rede/CORS — XHR morre com status 0 e o superagent devolve o
 *   "Request has been terminated..." em inglês;
 * * ação que o workflow do objeto não oferece — o CMFCore manda "No workflow
 *   provides the '${action_id}' action." com a interpolação literal (acontece
 *   quando o dropdown ainda mostra transições do conteúdo anterior, porque o
 *   reducer `workflow` do Volto é global e não chaveia por pathname).
 *
 * Nos dois casos trocamos o texto cru por instrução clara em pt-br.
 */

/** Título do toast quando a transição é recusada. */
export const TRANSITION_FAILURE_TITLE = 'Não foi possível mudar o estado';

/** Usado quando a resposta falhou mas não trouxe mensagem aproveitável. */
export const TRANSITION_FAILURE_FALLBACK =
  'Erro inesperado ao aplicar a transição.';

/**
 * Falha de rede/CORS não é recusa de negócio: o pedido nem chegou (ou a
 * resposta se perdeu). O superagent marca essas requisições com `crossDomain`
 * e `status 0` (XHR abortado pelo navegador) — o texto cru "Request has been
 * terminated..." não ajudaria o editor, então trocamos por instrução clara.
 */
export const TRANSITION_NETWORK_FAILURE_MESSAGE =
  'A requisição não chegou ao servidor (erro de rede ou permissão de origem ' +
  'CORS). Verifique a conexão e se o backend aceita a origem, depois tente ' +
  'novamente.';

/**
 * O dropdown lista transições do reducer global; navegando de um conteúdo
 * para outro as opções antigas podem sobrar e o POST acabar com uma ação que
 * o workflow do objeto não oferece. O CMFCore responde "No workflow provides
 * the '${action_id}' action." — com a variável literal, o que parece bug.
 */
export const TRANSITION_ACTION_NOT_FOUND_MESSAGE =
  'A ação de workflow não está disponível para este conteúdo: o estado atual ' +
  'deste item não oferece essa transição. Recarregue a página para atualizar ' +
  'as opções e tente novamente.';

const ehFalhaDeRede = (erro: any): boolean =>
  Boolean(
    erro?.crossDomain || erro?.status === 0 || erro?.code === 'ECONNABORTED',
  );

const normalizaMensagem = (bruta: any): string | null => {
  if (typeof bruta !== 'string') return null;
  const msg = bruta.trim();
  if (!msg) return null;
  if (msg.includes('No workflow provides')) {
    return TRANSITION_ACTION_NOT_FOUND_MESSAGE;
  }
  return msg;
};

export const transitionErrorMessage = (acaoOuErro: any): string | null => {
  // Ação `${TYPE}_FAIL` resolvida — o erro vive em `action.error`.
  const ehAcaoDeFalha =
    acaoOuErro && typeof acaoOuErro === 'object' && 'error' in acaoOuErro;
  const alvo = ehAcaoDeFalha ? acaoOuErro.error : acaoOuErro;

  if (ehFalhaDeRede(alvo)) return TRANSITION_NETWORK_FAILURE_MESSAGE;

  const bruta =
    alvo?.response?.body?.error?.message ??
    (!alvo?.response ? alvo?.message : undefined);
  const normalizada = normalizaMensagem(bruta);

  const falhou =
    ehAcaoDeFalha ||
    Boolean(
      alvo?.response || alvo?.status || alvo?.statusCode || alvo?.message,
    );
  if (normalizada) return normalizada;
  return falhou ? TRANSITION_FAILURE_FALLBACK : null;
};

export default transitionErrorMessage;
